// src/components/Modal.jsx
// Brutalist modal component
import { useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const Modal = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    showCloseButton = true,
    closeOnBackdrop = true
}) => {
    const modalRef = useRef(null);

    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-7xl'
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            modalRef.current?.focus();
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
                className="flex min-h-screen items-center justify-center p-4"
                onClick={handleBackdropClick}
            >
                <div className="fixed inset-0 bg-black/70 modal-backdrop" />

                <div
                    ref={modalRef}
                    tabIndex={-1}
                    className={`relative bg-surface border border-line rounded-xl shadow-elevated w-full ${sizeClasses[size]} animate-scale-in`}
                >
                    {/* Header */}
                    {(title || showCloseButton) && (
                        <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
                            <h3 className="font-mono text-sm font-bold text-white uppercase tracking-wide">
                                {title}
                            </h3>
                            {showCloseButton && (
                                <button
                                    onClick={onClose}
                                    className="p-1 rounded-md text-gray-500 hover:text-white hover:bg-slate-100 transition-colors"
                                    aria-label="Close modal"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Content */}
                    <div className="p-5">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ModalForm wrapper for forms - Brutalist
export const ModalForm = ({
    isOpen,
    onClose,
    onSubmit,
    title,
    children,
    submitLabel = 'SAVE',
    cancelLabel = 'CANCEL',
    isLoading = false,
    size = 'md'
}) => {
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (onSubmit) {
            await onSubmit(e);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size={size} closeOnBackdrop={false}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {children}

                <div className="flex justify-end gap-2 pt-4 border-t border-line">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="btn-ghost px-4 py-2"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="btn-primary px-4 py-2"
                    >
                        {isLoading ? (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 border-2 border-black/40 border-t-black rounded-full animate-spin" />
                                SAVING...
                            </div>
                        ) : (
                            submitLabel
                        )}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

// ModalConfirm for confirmation dialogs - Brutalist
export const ModalConfirm = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'CONFIRM ACTION',
    message,
    confirmLabel = 'CONFIRM',
    cancelLabel = 'CANCEL',
    type = 'danger' // 'danger' | 'warning' | 'info'
}) => {
    const typeStyles = {
        danger: 'bg-red-600 hover:bg-red-500 text-on-accent shadow-[0_0_20px_-6px_rgba(220,38,38,0.5)]',
        warning: 'bg-amber-500 hover:bg-amber-400 text-slate-900',
        info: 'bg-primary-gradient text-black shadow-glow hover:brightness-110'
    };

    const handleConfirm = () => {
        onConfirm();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
            <div className="mb-6">
                <p className="font-mono text-sm text-gray-300 leading-relaxed">{message}</p>
            </div>

            <div className="flex justify-end gap-2">
                <button
                    onClick={onClose}
                    className="btn-ghost px-4 py-2"
                >
                    {cancelLabel}
                </button>
                <button
                    onClick={handleConfirm}
                    className={`btn px-4 py-2 ${typeStyles[type]}`}
                >
                    {confirmLabel}
                </button>
            </div>
        </Modal>
    );
};

export default Modal;
